import { getUrlParam, HAS_WINDOW } from '@eventbrite/feature-detection';
import { formatUrl, NullableUrlParams } from 'url-lib';

export const getCheckoutWidgetLanguageUrl = (locale: string) => {
    if (!HAS_WINDOW) {
        return null;
    }
    const originalURL = new URL(window.location.href);
    originalURL.searchParams.set('lang', locale);

    return originalURL.toString();
};

/**
 * /checkout-widget-ajax
 */
export const getCheckoutWidgetAjaxUrl = (eventId: string) =>
    `/checkout-widget-ajax/${eventId}/`;
export const getPaymentHistoryUrl = (currentTime: number) =>
    `/checkout-widget-ajax/payment_history/?cb=${currentTime}`;
export const getLastPaymentStatusUrl = (currentTime: number) =>
    `/checkout-widget-ajax/last_payment_status/?cb=${currentTime}`;

/**
 * /auth
 */
export const _getGenerateGuestTokenUrl = () => '/api/v3/auth/guest_tokens/';
export const _getLogoutUrl = () => '/api/v3/auth/logout/';

/**
 * /events
 */
export const getDeliveryMethodsUrl = (eventId: string) =>
    `/api/v3/events/${eventId}/delivery_methods/`;
export const getAddToWaitlistUrl = (eventId: string) =>
    `/api/v3/events/${eventId}/waitlist/add/`;
export const getPaymentConstraintsByEventUrl = (eventId: string) =>
    `/api/v3/events/${eventId}/ticket_classes/?expand=payment_constraints`;
export const getPaymentConstraintsByTicketUrl = (
    eventId: string,
    ticketClassId: string,
) =>
    `/api/v3/events/${eventId}/ticket_classes/${ticketClassId}/?expand=payment_constraints`;
export const getAvailablePaymentMethodIssuersInformationUrl = (
    eventId: string,
) => `/api/v3/events/${eventId}/issuers_information/`;
export const getCannedQuestionsUrl = (eventId: string) =>
    `/api/v3/events/${eventId}/canned_questions/`;
export const getCustomQuestionsUrl = (eventId: string) =>
    `/api/v3/events/${eventId}/questions/`;
export const getSpecialInstructionsUrl = (eventId: string) =>
    `/api/v3/events/${eventId}/ticket_buyer_settings/`;
export const getShippingRatesUrl = ({
    eventId,
    currency,
    envCountryCode,
    orgId,
}: {
    eventId: string;
    currency: string;
    envCountryCode: string;
    orgId?: string;
}) => {
    let url = `/api/v3/events/${eventId}/shipping_rates/?country=${envCountryCode}&currency=${currency}`;
    if (orgId) {
        url = `${url}&organization_id=${orgId}`;
    }
    return url;
};
export const getExpressCheckoutConditionsUrl = (eventId: string) =>
    `/api/v3/events/${eventId}/express_checkout_conditions/`;

/**
 * /series
 */
export const SERIES_PAGE_SIZE = 12;

export type SeriesDateFilter = Record<string, unknown>;

export const DEFAULT_SERIES_DATE_FILTER: SeriesDateFilter = {
    time_filter: 'current_future',
};

export const DEFAULT_SERIES_EXPANSIONS = [
    'series_dates',
    'ticket_availability',
    'event_sales_status',
    'venue',
];

export const getSeriesEventsUrl = ({
    eventId,
    expansions,
    pageSize,
    continuationToken,
    dateFilter,
}: {
    eventId?: string;
    expansions?: Array<string>;
    pageSize?: number;
    continuationToken?: string;
    dateFilter?: SeriesDateFilter;
} = {}) =>
    formatUrl(`/api/v3/series/${eventId}/events`, {
        ...dateFilter,
        expand: expansions && expansions.join(','),
        page_size: pageSize,
        continuation: continuationToken,
    });

/**
 * /missive
 */
export const _getMissiveOrgMarketingOptInUrl = (organizationId: string) =>
    `/api/v3/missive/organization/${organizationId}/contacts/`;

/**
 * /organizer
 */
export const _getOrganizerUrl = (organizationId: string) =>
    `/api/v3/organizers/${organizationId}/`;

/**
 * /orders
 */
export const BASE_ORDER_EXPANSION = 'questions,survey,registration_form';

// These expansions is used to get extra order data and survey responses for Transfers and Add Attendees flows
export const AUGMENTED_INFO_EXPANSION = 'augmented_info';
export const SURVEY_RESPONSES_EXPANSION = 'survey_responses';
export const SURVEY_EXPANSION = 'survey';
export const ATTENDEES_EXPANSION = 'attendees';

const expand = (...expansions: Array<string>) =>
    expansions.filter(Boolean).join(',');

export const _getOrderUrl = (orderId: string) =>
    `/api/v3/orders/${orderId}/?expand=${expand(
        BASE_ORDER_EXPANSION,
        AUGMENTED_INFO_EXPANSION,
        SURVEY_RESPONSES_EXPANSION,
        ATTENDEES_EXPANSION,
    )}`;

export const _getAbandonOrderUrl = (orderId: string) =>
    `/api/v3/orders/${orderId}/abandon/`;
export const _getOrderAttendeesUrl = (
    orderId: string,
    { expansion = '', page = '' }: { expansion?: string; page?: string } = {},
) => `/api/v3/orders/${orderId}/attendees/?expand=${expansion}&page=${page}`;
export const _getAvailablePaymentMethodsUrl = (
    eventId: string,
    orderId: string,
) =>
    `/api/v3/events/${eventId}/orders/${orderId}/payment_options/?include_user_instruments=true`;

export const _getCreateOrderUrl = () =>
    `/api/v3/orders/create/?expand=${expand(BASE_ORDER_EXPANSION)}`;
export const _getPlaceOrderUrl = (orderId: string) =>
    `/api/v3/orders/${orderId}/place/?expand=ticket_buyer_settings`;
export const _getPlacePayInUrl = (eventId: string) =>
    `/api/v3/events/${eventId}/payin/`;
export const _getResendEmailUrl = (orderId: string) =>
    `/api/v3/orders/${orderId}/resend_confirmation_email/`;
export const _getSavePaymentMethodUrl = (orderId: string) =>
    `/api/v3/orders/${orderId}/payment_methods/`;
export const _getClearPaymentMethodUrl = (orderId: string) =>
    `/api/v3/orders/${orderId}/payment_methods/`;
export const _getUpdateOrderUrl = (
    orderId: string,
    { expansion }: { expansion?: string } = {},
) => `/api/v3/orders/${orderId}/${expansion ? '?expand=' + expansion : ''}`;
export const _updateEmailPreferencesUrl = (orderId: string) =>
    `api/v3/orders/${orderId}/email_preferences/`;
export const _getOrderEligibilityUrl = (orderId: string) =>
    `/api/v3/orders/${orderId}/eligibility/`;

export enum OrderErrorType {
    bestAvailableNotFoundError = 'ERROR_NO_MATCH_FOUND',
    payeeAccountError = 'PAYEE_ACCOUNT_ERROR',
    paypalPermissionError = 'PERMISSION_ERROR',
}

/** @deprecated use OrderErrorType.bestAvailableNotFoundError */
export const BEST_AVAILABLE_NOT_FOUND_ERROR =
    OrderErrorType.bestAvailableNotFoundError;

/** @deprecated use OrderErrorType.payeeAccountError */
export const PAYEE_ACCOUNT_ERROR = OrderErrorType.payeeAccountError;

/** @deprecated use OrderErrorType.paypalPermissionError */
export const PAYPAL_PERMISSION_ERROR = OrderErrorType.paypalPermissionError;

/**
 * /payments
 */
export const _getBraintreeClientTokenUrl = () =>
    '/api/v3/payments/braintree/client_token/';
export const _getBraintreeNonceUrl = () => '/api/v3/payments/braintree/nonce/';

/**
 * /queue_rpc
 */
export const _getQueueAcquireUrl = (
    queueServer: string,
    queueId: string,
    step: string,
) => `${queueServer}/queue_rpc/acquire/${queueId}/?step=${step}`;
export const _getQueueRenewUrl = (
    queueServer: string,
    clientId: string,
    queueId: string,
    isLive: unknown,
    allocationTime: unknown,
    hashCode: string,
    step: string,
) =>
    `${queueServer}/queue_rpc/renew/${clientId}/${queueId}/${isLive}/${allocationTime}/${hashCode}/?step=${step}`;
export const _getQueueRefreshUrl = (
    queueServer: string,
    clientId: string,
    step: string,
) => `${queueServer}/queue_rpc/renew/${clientId}/?step=${step}`;
export const _getQueueFreeUrl = (
    clientId: string,
    allocationTime: string,
    hashCode: string,
) => `/queue_rpc/free/${clientId}/${allocationTime}/${hashCode}/`;

/**
 * /reserved
 */
export const _getBestAvailableUrl = (eventId: string) =>
    `/reserved/ajax/best_available/${eventId}/`;

/**
 * /users
 */
export const _getVaultInstrumentsUrl = (userId: string) =>
    `/api/v3/users/${userId}/vault_instruments/`;
export const _getCreditCardVaultInstrumentsUrl = (userId: string) =>
    `/api/v3/users/${userId}/vault_instruments/?instrument_types=CREDIT_CARD`;
export const _getUserAccountDetails = () => '/api/v3/users/lookup/';
export const _getCurrentUserAccountDetailsUrl = () => '/api/v3/users/me/';

/**
 * group registration
 */
export const _getGroupSearchUrl = (eventId: string, querySearch: string) =>
    `/api/v3/events/${eventId}/teams/search/?term=${querySearch}`;
export const _validateGroupPasswordUrl = (eventId: string, teamId: string) =>
    `/api/v3/events/${eventId}/teams/${teamId}/check_password/`;
export const _getGroupCreateUrl = (eventId: string) =>
    `/api/v3/events/${eventId}/teams/create/`;

/**
 * sponsorship assets
 */
export const _getSponsorshipAssetsUrl = (
    eventId: string,
    params?: NullableUrlParams,
) =>
    formatUrl(`/api/v3/sponsor/events/${eventId}/assets/`, {
        ...params,
    });

/**
 * tax policies
 */
export const _getTaxPolicies = (country: string) =>
    `/api/v3/tax_policies/?tax_country=${country}`;

export const _validateTaxIdCountry = (
    eventId: string,
    country: string,
    taxId: string,
) =>
    formatUrl('/ajax/tax/validate_id_and_country_exemptions', {
        event_id: eventId,
        country: country,
        tax_id: taxId,
    });

export const _validateQstTaxIdCountry = () =>
    '/api/v3/gts_tax_exemption_validation/';

/*
 * New Order Service
 */

export type TlzEnvironment = 'dev' | 'qa' | 'preprod' | 'prod';

export const checkoutBFFBaseUrlForEnvironment: Record<TlzEnvironment, string> =
    {
        prod: 'https://checkoutfairy.ernt4vxu.ext.eventbrite.com',
        preprod: 'https://checkoutfairy.g23n4xhx.ext.evbstage.com',
        qa: 'https://checkoutfairy.v3tuevyu.ext.evbqa.com',
        dev: 'https://checkoutfairy.3ylaays2.ext.evbdev.com',
    };

const isBffVersionOverrideAllowedInTlzEnvironment: Record<
    TlzEnvironment,
    boolean
> = {
    prod: false,
    preprod: false,
    qa: true,
    dev: true,
};

const getBffBaseUrl = ({ env }: { env: TlzEnvironment }) => {
    const overridenBffVersion = isBffVersionOverrideAllowedInTlzEnvironment[env]
        ? getUrlParam('bffVersion')
        : null;
    const bffVersion = overridenBffVersion ?? 'main';
    const requestHost = checkoutBFFBaseUrlForEnvironment[env];

    return `${requestHost}/${bffVersion}`;
};

export const _getOrderStatusUrl = ({
    env,
    eventId,
    orderId,
}: {
    env: TlzEnvironment;
    eventId: string;
    orderId: string;
}) => {
    const baseUrl = getBffBaseUrl({
        env,
    });
    return `${baseUrl}/event/${eventId}/order/${orderId}/status/`;
};

export enum OrderDocumentStatus {
    abandoned = 'ABANDONED',
    cancelled = 'CANCELLED',
    confirmed = 'CONFIRMED',
    draft = 'DRAFT',
    finalized = 'FINALIZED',
}

export const INFO_REQUESTED_ORDER_SERVICE_INPUT_PLACEHOLDER = 'Info Requested';

export const _getNosRolloutUrl = ({
    organizationId,
    eventId,
}: {
    organizationId: string;
    eventId: string;
}) => `/api/v3/rollout/${organizationId}/${eventId}/`;

export const _getOrderComposedStatusUrl = ({
    env,
    eventId,
    orderId,
}: {
    env: TlzEnvironment;
    eventId: string;
    orderId: string;
}) => {
    const baseUrl = getBffBaseUrl({
        env,
    });
    return `${baseUrl}/event/${eventId}/order/${orderId}/composedStatus/`;
};
